import { useState, useRef } from "react";
import { supabase } from "../supabaseClient";

const CATEGORIES = [
  { key: "med_list", icon: "📋", label: "Medication List" },
  { key: "discharge", icon: "🏥", label: "Hospital Discharge Summary" },
  { key: "rx", icon: "💊", label: "Prescription" },
  { key: "md_orders", icon: "📝", label: "MD Orders" },
  { key: "avs", icon: "🏃", label: "After Visit Summary (AVS)" },
  { key: "lab", icon: "🔬", label: "Lab Results" },
  { key: "imaging", icon: "📷", label: "Imaging Report" },
  { key: "post_op", icon: "🔪", label: "Post-Op Notes" },
  { key: "audio", icon: "🎙️", label: "Visit Voice Recording" },
  { key: "other", icon: "📄", label: "Other" },
];

const ACCEPT =
  ".pdf,.jpg,.jpeg,.png,.heic,.webp,.mp3,.mp4,.m4a,.wav,.aac,.webm,.ogg," +
  "image/*,application/pdf,audio/*";

const slugify = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

const fileIcon = (mimeType, ext) => {
  if (/^audio\//.test(mimeType)) return "🎙️";
  if (mimeType === "application/pdf" || ext === "pdf") return "📄";
  if (/^image\//.test(mimeType)) return "🖼️";
  return "📄";
};

const formatBytes = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const getEffectiveDocId = (docId) => {
  if (docId) return docId;
  const cached = sessionStorage.getItem("temp_doc_session");
  if (cached) return cached;
  const id = "temp_" + Date.now();
  sessionStorage.setItem("temp_doc_session", id);
  return id;
};

const DocumentUpload = ({ docId, formType, onUploaded }) => {
  const [docs, setDocs] = useState([]);
  const [pickedFile, setPickedFile] = useState(null);
  const [pickedCategory, setPickedCategory] = useState("");
  const [otherLabel, setOtherLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPickedFile(file);
    setError(null);
    e.target.value = "";  // allow picking the same file again later
  };

  const cancelPick = () => {
    setPickedFile(null);
    setPickedCategory("");
    setOtherLabel("");
  };

  const doUpload = async () => {
    if (!pickedFile || !pickedCategory) {
      setError("Pick a file AND a category before uploading.");
      return;
    }
    if (pickedCategory === "other" && !otherLabel.trim()) {
      setError("Type a label for this 'Other' document.");
      return;
    }
    setUploading(true);
    setError(null);
    setProgress(10);

    const cat = CATEGORIES.find((c) => c.key === pickedCategory);
    const label = pickedCategory === "other" ? otherLabel.trim() : cat.label;
    const ext = (pickedFile.name.split(".").pop() || "bin").toLowerCase();
    const slug = slugify(label || cat.label || "doc");
    const ts = Date.now();
    const path = `${formType}/${getEffectiveDocId(docId)}/${slug}_${ts}.${ext}`;

    setProgress(40);
    const { error: upErr } = await supabase.storage
      .from("clinical-documents")
      .upload(path, pickedFile, {
        contentType: pickedFile.type || "application/octet-stream",
        upsert: false,
      });
    setProgress(90);

    if (upErr) {
      setError(`Upload failed: ${upErr.message}`);
      setUploading(false);
      setProgress(0);
      return;
    }

    const entry = {
      category: cat.key,
      label,
      filename: pickedFile.name,
      storagePath: path,
      mimeType: pickedFile.type,
      sizeBytes: pickedFile.size,
      uploadedAt: new Date().toISOString(),
    };
    const next = [...docs, entry];
    setDocs(next);
    onUploaded?.(next);

    setUploading(false);
    setProgress(0);
    cancelPick();
  };

  const removeDoc = async (idx) => {
    const doomed = docs[idx];
    if (doomed?.storagePath) {
      // Best-effort cleanup; ignore failures (file may already be gone).
      await supabase.storage.from("clinical-documents")
        .remove([doomed.storagePath]).catch(() => {});
    }
    const next = docs.filter((_, i) => i !== idx);
    setDocs(next);
    onUploaded?.(next);
  };

  return (
    <div style={{ marginTop: 12, padding: 14,
      background: "#F5F0FA", borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center",
        gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>
          📎 Attached Documents
        </span>
        <label style={{ cursor: "pointer", background: "#6B2D9E",
          color: "#fff", padding: "5px 14px", borderRadius: 6,
          fontSize: 13 }}>
          + Add File
          <input ref={inputRef} type="file"
            accept={ACCEPT}
            style={{ display: "none" }}
            onChange={onFileChosen} />
        </label>
        {docs.length > 0 && (
          <span style={{ fontSize: 12, color: "#10B981", fontWeight: 600 }}>
            ✓ {docs.length} document{docs.length === 1 ? "" : "s"} attached
          </span>
        )}
      </div>

      {/* Category prompt + upload trigger */}
      {pickedFile && (
        <div style={{ background: "#fff", border: "1.5px solid #C7D2FE",
          borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600,
            color: "#1F2937" }}>
            Selected: <span style={{ fontWeight: 400 }}>
              {pickedFile.name} ({formatBytes(pickedFile.size)})
            </span>
          </p>
          <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 600,
            color: "#4B5563" }}>Category</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6,
            marginBottom: 10 }}>
            {CATEGORIES.map((c) => (
              <button key={c.key} type="button"
                onClick={() => setPickedCategory(c.key)}
                style={{
                  background: pickedCategory === c.key ? "#6B2D9E" : "#fff",
                  color: pickedCategory === c.key ? "#fff" : "#1F2937",
                  border: `1.5px solid ${pickedCategory === c.key
                    ? "#6B2D9E" : "#E5E7EB"}`,
                  borderRadius: 16, padding: "4px 12px",
                  fontSize: 12, cursor: "pointer",
                }}>
                {c.icon} {c.label}
              </button>
            ))}
          </div>
          {pickedCategory === "audio" && (
            <p style={{ margin: "0 0 8px", fontSize: 11,
              fontStyle: "italic", color: "#6B7280" }}>
              💡 Record with Voice Memo on iPhone, then tap Add File to attach.
            </p>
          )}
          {pickedCategory === "other" && (
            <input type="text" value={otherLabel}
              onChange={(e) => setOtherLabel(e.target.value)}
              placeholder='Custom label (e.g. "Wound culture report")'
              style={{ width: "100%", padding: "6px 10px",
                border: "1px solid #d1d5db", borderRadius: 6,
                marginBottom: 8, fontSize: 13 }} />
          )}
          {error && (
            <p style={{ margin: "0 0 8px", color: "#DC2626", fontSize: 12 }}>
              {error}
            </p>
          )}
          {uploading && (
            <div style={{ height: 6, background: "#E5E7EB", borderRadius: 4,
              overflow: "hidden", marginBottom: 8 }}>
              <div style={{ width: `${progress}%`, height: "100%",
                background: "#10B981", transition: "width 0.2s" }} />
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={doUpload} disabled={uploading}
              style={{ background: "#10B981", color: "#fff", border: "none",
                borderRadius: 6, padding: "6px 14px", fontSize: 13,
                cursor: uploading ? "not-allowed" : "pointer",
                opacity: uploading ? 0.6 : 1 }}>
              {uploading ? "Uploading…" : "Upload"}
            </button>
            <button type="button" onClick={cancelPick} disabled={uploading}
              style={{ background: "#fff", color: "#6B2D9E",
                border: "1.5px solid #6B2D9E", borderRadius: 6,
                padding: "6px 14px", fontSize: 13, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List of uploaded docs */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {docs.map((doc, i) => {
          const ext = (doc.filename.split(".").pop() || "").toLowerCase();
          const cat = CATEGORIES.find((c) => c.key === doc.category);
          return (
            <div key={i} style={{ display: "flex", alignItems: "center",
              gap: 10, background: "#fff", padding: "8px 12px",
              borderRadius: 6, border: "1px solid #E5E7EB" }}>
              <span style={{ fontSize: 18 }}>{fileIcon(doc.mimeType, ext)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600,
                  color: "#6B2D9E" }}>
                  {cat?.icon || ""} {doc.label}
                </div>
                <div style={{ fontSize: 12, color: "#1F2937",
                  whiteSpace: "nowrap", overflow: "hidden",
                  textOverflow: "ellipsis" }}>{doc.filename}</div>
              </div>
              <span style={{ fontSize: 11, color: "#6B7280" }}>
                {formatBytes(doc.sizeBytes)}
              </span>
              <button type="button" onClick={() => removeDoc(i)}
                aria-label="Remove document"
                style={{ background: "none", border: "none",
                  color: "#DC2626", cursor: "pointer", fontSize: 18,
                  padding: "0 4px", lineHeight: 1 }}>×</button>
            </div>
          );
        })}
        {docs.length === 0 && !pickedFile && (
          <p style={{ margin: 0, fontSize: 12, color: "#aaa" }}>
            No documents attached. Tap "+ Add File" above.
          </p>
        )}
      </div>
    </div>
  );
};

export default DocumentUpload;
